import { gql } from '@apollo/client'
import {
  LEGAL_FORMALITY_FRAGMENT,
  LEGAL_FORMALITIES_DOCUMENT_FRAGMENT,
  LEGAL_DEPOSIT_FRAGMENT,
} from '../Fragments'

export const UPDATE_LEGAL_FORMALITY_MUTATION = gql`
  mutation updateLegalFormality($id: Int!, $assignedUserId: Int) {
    updateLegalFormality(id: $id, assignedUserId: $assignedUserId) {
      legalFormality {
        id
        status
        assignedUser {
          id
          email
        }
      }
    }
  }
`

export const LEGAL_FORMALITY_ADD_NOTE_MUTATION = gql`
  mutation legalFormalityAddNote($id: Int!, $text: String!) {
    legalFormalityAddNote(id: $id, text: $text) {
      legalFormality {
        id
        internalNotes
      }
    }
  }
`

export const NEXT_FORMALITY_STEP_MUTATION = gql`
  mutation legalFormalityNextStep(
    $id: Int!
    $authorityRefusalReason: String
    $authorityRefuse: Boolean
    $authorityRedrop: Boolean
    $submissionId: String
    $metadatum: String
    $depositProvider: String
  ) {
    legalFormalityNextStep(
      id: $id
      authorityRefusalReason: $authorityRefusalReason
      authorityRefuse: $authorityRefuse
      authorityRedrop: $authorityRedrop
      submissionId: $submissionId
      metadatum: $metadatum
      depositProvider: $depositProvider
    ) {
      legalFormality {
        ...LegalFormalityFragment
      }
    }
  }
  ${LEGAL_FORMALITY_FRAGMENT}
`

export const RELOAD_DOCUMENTS_FORMALITY_MUTATION = gql`
  mutation legalFormalityReloadDocuments($id: Int!) {
    legalFormalityEditFlow(id: $id) {
      legalFormality {
        id
        status
      }
    }
  }
`

export const EDIT_FORMALITY_FLOW_MUTATION = gql`
  mutation legalFormalityEditFlow(
    $id: Int!
    $abort: Boolean
    $rollback: Boolean
  ) {
    legalFormalityEditFlow(id: $id, abort: $abort, rollback: $rollback) {
      legalFormality {
        id
        status
      }
    }
  }
`

export const CORRECT_FORMALITY_QUESTION_MUTATION = gql`
  mutation legalFormalityFormCorrect(
    $id: Int!
    $field: String!
    $correction: String!
    $correctAll: Boolean
    $clear: Boolean
  ) {
    legalFormalityFormCorrect(
      legalFormalityId: $id
      field: $field
      correction: $correction
      correctAll: $correctAll
      clear: $clear
    ) {
      legalFormality {
        id
        validatedFormData
      }
    }
  }
`

export const LEGAL_FORMALITIES_QUERY = gql`
  query getLegalFormalities($contactId: ID!) {
    legalFormalities(contactId: $contactId) {
      id
      status
      lastStatusesHistorics {
        final
        lastDate
      }
      authorityRefusalReason
      purchase {
        identificationToken
      }
    }
  }
`

export const LEGAL_FORMALITY_DOCUMENT_UPLOAD_MUTATION = gql`
  mutation legalFormalityDocumentUpload(
    $purchaseId: Int!
    $documentType: DocumentType!
    $legalDocumentableType: String
    $legalDocumentableId: String
    $fileRecto: Upload
    $fileVerso: Upload
    $removedUpload: String
  ) {
    legalFormalityDocumentUpload(
      purchaseId: $purchaseId
      documentType: $documentType
      legalDocumentableType: $legalDocumentableType
      legalDocumentableId: $legalDocumentableId
      fileRecto: $fileRecto
      fileVerso: $fileVerso
      removedUpload: $removedUpload
    ) {
      legalFormalityDocument {
        ...LegalFormalitiesDocumentFragment
      }
    }
  }
  ${LEGAL_FORMALITIES_DOCUMENT_FRAGMENT}
`

export const LEGAL_FORMALITY_QUERY = gql`
  query getLegalFormality($id: ID, $purchaseId: ID) {
    legalFormality(id: $id, purchaseId: $purchaseId) {
      ...LegalFormalityFragment
    }
  }
  ${LEGAL_FORMALITY_FRAGMENT}
`

export const LEGAL_FORMALITY_DOCUMENT_UPDATE_CHECKS_MUTATION = gql`
  mutation legalFormalityDocumentUpdateChecks(
    $legalDocumentId: ID!
    $checks: [CheckInput!]!
    $validateDocument: Boolean
  ) {
    legalFormalityDocumentUpdateChecks(
      legalDocumentId: $legalDocumentId
      checks: $checks
      validateDocument: $validateDocument
    ) {
      legalFormalityDocument {
        ...LegalFormalitiesDocumentFragment
      }
    }
  }
  ${LEGAL_FORMALITIES_DOCUMENT_FRAGMENT}
`

export const LEGAL_FORMALITY_DOCUMENT_MERGE_MUTATION = gql`
  mutation legalFormalityDocumentMerge(
    $purchaseId: Int!
    $documentType: DocumentType!
    $targetType: String
    $targetId: String
    $force: Boolean!
    $aiMerge: Boolean
  ) {
    legalFormalityDocumentMerge(
      purchaseId: $purchaseId
      documentType: $documentType
      targetType: $targetType
      targetId: $targetId
      force: $force
      aiMerge: $aiMerge
    ) {
      legalFormalityDocument {
        ...LegalFormalitiesDocumentFragment
      }
    }
  }
  ${LEGAL_FORMALITIES_DOCUMENT_FRAGMENT}
`

export const LEGAL_FORMALITY_ARCHIVE_DOWNLOAD_MUTATION = gql`
  mutation legalFormalityArchiveDownload($id: Int!) {
    legalFormalityArchiveDownload(legalFormalityId: $id) {
      success
      error
    }
  }
`

export const LEGAL_FORMALITY_FIELD_OPTIONS_QUERY = gql`
  query getLegalFormalityFieldOptions($id: ID!, $fieldNames: [String!]!) {
    legalFormalityFieldOptions(id: $id, fieldNames: $fieldNames) {
      fieldOptions
    }
  }
`

export const SEND_MAIL_LEGAL_FORMALITY_MUTATION = gql`
  mutation legalFormalitySendMail($id: Int!, $status: String!) {
    legalFormalitySendMail(id: $id, status: $status) {
      success
      error
    }
  }
`

export const STEP_BACK_LEGAL_FORMALITY_MUTATION = gql`
  mutation legalFormalityStepBack($id: Int!, $status: String!) {
    legalFormalityStepBack(id: $id, status: $status) {
      success
      error
    }
  }
`

export const RESET_LEGAL_FORMALITY_MUTATION = gql`
  mutation legalFormalityReset($id: Int!, $resetType: String!) {
    legalFormalityReset(id: $id, resetType: $resetType) {
      success
      error
    }
  }
`

export const GENERATE_DOC_LEGAL_FORMALITY_MUTATION = gql`
  mutation legalFormalityGenerateDoc(
    $id: Int!
    $docType: String!
    $staffId: String
  ) {
    legalFormalityGenerateDoc(id: $id, docType: $docType, staffId: $staffId) {
      success
      error
    }
  }
`

export const LEGAL_FORMALITY_FORM_FILE_CONTENT = gql`
  query LegalFormalityFormFileContent($id: ID!, $url: String!) {
    legalFormalityFormFileContent(id: $id, url: $url) {
      content
      filename
      contentType
    }
  }
`

export const UPDATE_DOCUMENT_MARKDOWN_MUTATION = gql`
  mutation legalFormalityUpdateDocumentMarkdown(
    $legalDocumentId: ID!
    $content: String!
  ) {
    legalFormalityUpdateDocumentMarkdown(
      legalDocumentId: $legalDocumentId
      content: $content
    ) {
      legalFormalityDocument {
        ...LegalFormalitiesDocumentFragment
      }
      error
    }
  }
  ${LEGAL_FORMALITIES_DOCUMENT_FRAGMENT}
`

export const LEGAL_FORMALITY_LEGAL_DEPOSIT_CREATE_MUTATION = gql`
  mutation legalFormalityLegalDepositCreate($id: Int!) {
    legalFormalityLegalDepositCreate(id: $id) {
      legalFormality {
        ...LegalFormalityFragment
      }
    }
  }
  ${LEGAL_FORMALITY_FRAGMENT}
`

export const LEGAL_FORMALITY_LEGAL_DEPOSIT_UPDATE_MUTATION = gql`
  mutation legalFormalityLegalDepositUpdate(
    $id: Int!
    $providerRef: String
    $status: String
    $regularisation: Boolean
    $content: JSON
  ) {
    legalFormalityLegalDepositUpdate(
      id: $id
      providerRef: $providerRef
      status: $status
      regularisation: $regularisation
      content: $content
    ) {
      legalDeposit {
        ...LegalDepositFragment
      }
    }
  }
  ${LEGAL_DEPOSIT_FRAGMENT}
`
